import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';

import PlayIcon from "@material-ui/icons/PlayCircleFilled";
import MaterialTable from "@material-table/core";
import {
  TablePagination,
  IconButton, Tooltip, Button, Icon, useTheme,
} from "@material-ui/core";
import { ColumnWidth } from "../../../blocks/ColumnHelper";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import SystemContext from "../../../SystemContext";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect, LightTooltip } from '../../../controls';
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";
import SendFuelOrderDialog from "./FuelOrderDialogs/SendFuelOrderDialog";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import { useFuelOrders, useMoveOrderLinesToNewFuelOrder } from "../../../services/fuel";
import useColour, {getStatusColour} from '../../../services/settings/useColour';
import { getTemplate } from "../../../functions/TemplateHelperFunctions"
import AddFuelOrderDialog from "./FuelOrderDialogs/AddFuelOrderDialog";

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}


const FuelOrdersToReviewList = (_props) => {
  const props = {
    ..._props
  }
  const {classes} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const [state] = useState({
    canEdit: props.canEdit,
  });

  const [sendDialogStates, setSendDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'Send'
  });
  const [addFuelDialogStates, setAddFuelDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'New Fuel Order'
  });
  const [sendFuelDialogStates, setSendFuelDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'Send Fuel Order'
  });
  const [defaultValues, setDefaultValues] = useState({
    defaultTemplate: null,
    defaultAttachment: null,
  });

  const year = props.year;
  const month = props.month;
  const required = props.required;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleMoveOrderLinesToNewFuelOrder = useMoveOrderLinesToNewFuelOrder();
  const queryClient = useQueryClient();
  const extraKeys = [year, month];
  if (required === "review")
    extraKeys.push("review");

  const fuelOrders = useFuelOrders({tableRef, queryClient, extraKeys});

  const colours = useColour({code: "FUEL ORDERS TEXT"});
  const backgroundColours = useColour({code: "FUEL ORDERS BACKGROUND"});

  const refreshList = async () => {
    queryClient.invalidateQueries(["FuelOrders"]);
  }

  const getDefaultValues = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/fuelorders/defaults`);
      if (response.data && response.data.success) {
        setDefaultValues(response.data.defaultValues);
        return response.data.defaultValues;
      }

      if (!response.data) {
        console.log(`Could not retrieve Fuel Order Defaults. Server is not responding`);
        SystemData.alert("Error", `Could not retrieve Fuel Order Template. Server is not responding`);
      } else if (!response.data.success) {
        console.log(`Could not retrieve Fuel Order Defaults. Server returned ${response.data.error}`);
        SystemData.alert("Error", `Could not retrieve Fuel Order Template. Server returned ${response.data.error}`);
      }
    } catch (err) {
      console.log(`Could not retrieve Fuel Order Defaults. Server is not responding`);
      SystemData.alert("Error", `Could not retrieve Fuel Order Template. Server is not responding`);
    }
  }

  const handleSendNotificationClick = async (selectedRecords) => {
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "fuelorders", "FuelOrder");
      setDefaultValues(defaults);
    }

    let records = Array.isArray(selectedRecords) ? selectedRecords : [selectedRecords];
    const firstRecord = __.cloneDeep(records[0]);
    firstRecord["type"] = "notification";
    firstRecord["fuelSupplierUuid"] = firstRecord.companyUuid;
    records = __.filter(records, {airportCode: firstRecord.airportCode, companyUuid: firstRecord.companyUuid});

    const firstDepartureDate = firstRecord?.departureDate ? firstRecord.departureDate : null;
    const month = firstDepartureDate ? moment(firstDepartureDate, "YYYY-MM-DD") : moment();
    const subject = `Magma Aviation Fuel Order Cancellation Notification // ${firstRecord.airportCode} // ${month.format("MMM-YYYY")}`;

    const template = await getTemplate( SystemData, "fuelorders", "Fuel Order Cancellation", "cancellation", firstRecord.cancellationTemplate);
    // getTemplate will popup an alert if it cannot get a template, so no point in going further
    if (!template)
      return;

    firstRecord["orderTemplate"] = template;
    let lines = [];
    records.forEach(record => {
      const line =
      {
        reference: record.reference||'',
        locationAirportCode: record.airportCode,
        airlineCode: record.airlineCode,
        flightNo: record.flightNo,
        aircraftType: "B747F",
        aircraftTailNumber: record.aircraftId,
        departureDate: record.scheduledTimeUTC,
        scheduledDepartureTimeUTC: moment(record.scheduledTimeUTC).format("HH:mm"),
        direction: record.direction === "OUT" ? "Outbound" : "Inbound",
        reason: record.reason ?? "",
        tripLegCancelledServiceUuid: record.tripLegCancelledServiceUuid,
        fuelOrderUuid: record.fuelOrderUuid,
        tripLegUuid: record.tripLegUuid,
        volume: record.volume,
        destinationAirportCode: record.destinationAirportCode
      };
      lines.push(line);
    });


    firstRecord["lines"] = lines;
    if (firstRecord.orderTemplate) {
      const newTemplate = {
        ...firstRecord,
        orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        fuelOrderSubject: subject,
        carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : defaults.ccList,
      }
      const title = `Send Cancellation Notification${firstRecord ? " to '" + firstRecord.companyName + "'":""}${firstRecord.airportCode ? " at '" + firstRecord.airportCode + "'": ""}`;
      setSendDialogStates({...sendDialogStates, showDialog: true, record: {...(__.cloneDeep(newTemplate))}, parentRecord: null, title: title});
    }
  }

  const closeSendDialog = () => {
    setSendDialogStates({
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'Send'
    });
  }

  const handleCancelRequirementClick = async (selectedRecords) => {
    try {
      SystemData.toggleLoadingProgress(true, "Removing cancelled requirements");
      const response = await axios.put(`${apiSitePrefix}/fuelorders/cancelRequirements`, selectedRecords);
      SystemData.toggleLoadingProgress(false);
      if (!response.data) {
        SystemData.alert("Error", `Could remove cancelled requirements. Server is not responding`);
      } else if (response.data.success) {
        queryClient.invalidateQueries(["FuelOrders", 0, 0 , "review"]);
        queryClient.invalidateQueries(["FuelOrders", "Count", 0, 0 , "review"]);
      } else {
        SystemData.alert("Error", `Could not remove cancelled requirements. Server returned ${response.data.error}`);
      }
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      SystemData.alert("Error", `Could remove cancelled requirements. Server is not responding`);
    }
  }

  const handleIgnoreChangesClick = async (selectedRecords) => {
    try {
      SystemData.toggleLoadingProgress(true, "Ignoring changes");
      const response = await axios.put(`${apiSitePrefix}/fuelorders/ignoreChanges`, selectedRecords);
      SystemData.toggleLoadingProgress(false);
      if (!response.data) {
        SystemData.alert("Error", `Could not ignore changes. Server is not responding`);
      } else if (response.data.success) {
        queryClient.invalidateQueries(["FuelOrders", 0, 0 , "review"]);
        queryClient.invalidateQueries(["FuelOrders", "Count", 0, 0 , "review"]);
      } else {
        SystemData.alert("Error", `Could not ignore changes. Server returned ${response.data.error}`);
      }
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      SystemData.alert("Error", `Could not ignore changes. Server is not responding`);
    }
  }

  const handleSendOrderClick = async (rowData) => {
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "fuelorders", "FuelOrder");
      setDefaultValues(defaults);
    }

    const yearMonth = rowData?.tripNo ? rowData.tripNo.toString().substr(0,4) : null;
    const month = yearMonth ? moment(yearMonth, "YYMM") : moment();
    const fuelOrderSubject = `Magma Aviation Fuel Request // ${rowData.airportCode} // ${month.format("MMM-YYYY")}`;
    if (rowData.orderTemplate === undefined) {
      const template = await getTemplate( SystemData, "fuelorders", "Fuel Order", "order", rowData.fuelOrderTemplateUuid);
      // getTemplate will popup an alert if it cannot get a template, so no point in going further
      if (!template)
        return;
      rowData["orderTemplate"] = template;
    }
    if (rowData.orderTemplate) {
      const newFuelOrderTemplate = {
        ...rowData,
        orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        fuelOrderSubject,
        carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : defaults.ccList,
      }
      const title = `Send Fuel Order${rowData ? " to '" + rowData.companyName + "'":""}${rowData.airportCode ? " at '" + rowData.airportCode + "'": ""}`;
      setSendFuelDialogStates({...sendFuelDialogStates, showDialog: true, record: {...(__.cloneDeep(newFuelOrderTemplate))}, parentRecord: null, title: title});
    }
  }
  const closeSendFuelOrder = () => {
    setSendFuelDialogStates({
      ...addFuelDialogStates,
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'Send Fuel Order'
    });
  }

  const handleAmendOrderClick = async (selectedRecords) => {
    let records = Array.isArray(selectedRecords) ? selectedRecords : [selectedRecords];
    const firstRecord = __.cloneDeep(records[0]);
    firstRecord["fuelSupplierUuid"] = firstRecord.companyUuid;
    records = __.filter(records, {originAirportCode: firstRecord.originAirportCode, companyUuid: firstRecord.companyUuid});
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "fuelorders", "Fuel Orders");
      if (!defaults)
        return;
    }
    let lines = [];
    records.forEach(record => {
      const line =
        {
          tripLegUuid: record.tripLegUuid,
          reference: `${record.airlineCode}${record.flightNo}-${moment.utc(record.scheduledDepartureTimeUTC).format("MMDD")}`,
          locationAirportCode: record.originAirportCode,
          destinationAirportCode: record.destinationAirportCode,
          airlineCode: record.airlineCode,
          flightNo: record.flightNo,
          flightNumber: `${record.airlineCode}${record.flightNo}`,
          aircraftType: "B747F",
          aircraftTailNumber: record.aircraftId,
          arrivalDate: moment.utc(record.previousScheduledArrivalTimeUTC).format("YYYY-MM-DD"),
          scheduledArrivalTimeUTC: moment.utc(record.previousScheduledArrivalTimeUTC).format("HH:mm"),
          departureDate: moment.utc(record.scheduledDepartureTimeUTC).format("YYYY-MM-DD"),
          scheduledDepartureTimeUTC: moment.utc(record.scheduledDepartureTimeUTC).format("HH:mm"),
          fuelPolicy: "SECTOR",
          orderUuid: record.orderUuid
        }
        lines.push(line);
    });

    let fuelSupplierUuid = firstRecord.companyUuid;
    const newFuelOrderTemplate = {
      fuelOrderUuid: uuidv4(),
      airportCode: firstRecord.originAirportCode,
      orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
      lines:lines,
      status: "REQUIRED",
      tripNo: null,
      fuelOrderTemplateUuid: firstRecord.orderTemplate || defaults.defaultTemplate,
      attachmentUuid: defaultValues.defaultAttachment || defaults.defaultAttachment,
      fuelPolicy: "SECTOR",
      fuelSupplierUuid: fuelSupplierUuid ?? undefined,
      companyName: firstRecord.companyName
    }
    setAddFuelDialogStates({...addFuelDialogStates, showDialog: true, record: {...(__.cloneDeep(newFuelOrderTemplate))}, parentRecord: null, title: 'New Fuel Order'});
  }

  const closeAddFuelOrder = () => {
    setAddFuelDialogStates({
      ...addFuelDialogStates,
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'New Fuel Order'
    });
    queryClient.invalidateQueries(["FuelOrders", "Count", 0, 0, true]);
    queryClient.invalidateQueries(["FuelOrders", "Count", 0, 0, "REVIEW"]);
  }

  const selectionAllowed = (rowData) => {
    if (tableRef.current) {
      const data = tableRef.current.dataManager.data;
      if (data && Array.isArray(data)) {
        let selectedAirport = null;
        let companyUuid = null;
        let status = null;
        let changeEnabled = null;
        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          if (__.has(item.tableData, "checked") && item.tableData.checked) {
            selectedAirport = item.airportCode;
            companyUuid = item.companyUuid;
            status = item.status;
            changeEnabled = (['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${item.reason}`) > -1)
            break;
          }
        }
        if (selectedAirport) {
          if (changeEnabled) {
            if (['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData.reason}`) > -1) {
              return (rowData.airportCode === selectedAirport && rowData.companyUuid === companyUuid);
            }
          } else {
            if (['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData.reason}`) < 0) {
              return (rowData.airportCode === selectedAirport && rowData.companyUuid === companyUuid && rowData.status === status);
            }
          }
          return false;
        }
      }
      return true;
    }
  }

  const getColumns = (props) => {

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(110, [10,10], undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "At Airport",
        field: "airportCode",
        initialEditValue: "",
        sorting: true,
        editable: "never",
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportCode} />}>
            <span> {rowData.airportCode} </span>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,[10,10],undefined,undefined,undefined,undefined,false),
        title: "Flight Number",
        field: "orderFlightNo",
        render: (props) => (
          <div style={{ width: 162 }}>
            {props.orderAirlineCode}&nbsp;{props.orderFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100, undefined, undefined, undefined, undefined)),
        title: "Flight Date",
        field: "scheduledTimeUTC",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.scheduledTimeUTC).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.scheduledTimeUTC ? moment(rowData.scheduledTimeUTC).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(160, undefined, undefined, undefined, undefined)),
        title: "Scheduled Time",
        field: "scheduledTimeUTC",
        render: rowData => {
          const scheduledTime = rowData.scheduledTimeUTC;
          return (
            <div>{scheduledTime ? moment(scheduledTime).format("HH:mm") : ""} {rowData.direction === "OUT" ? `(Outbound ${rowData.airportCode})` : `(Inbound ${rowData.airportCode})`}</div>
          )
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined)),
        title: "Order Created On",
        field: "orderDate",
        render: rowData => {
          const displayDate = rowData.orderDate ? moment(rowData.orderDate).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(180, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Original Order Status",
        field: "status",
        editable: "never",
        emptyValue: "",
        fullWidth: true,
        cellStyle: (value, rowData) => {
          const backgroundColor = getStatusColour(backgroundColours, value);
          const color = getStatusColour(colours, value);
          const {cellStyle} = ColumnWidth(100);
          return ({
            ...cellStyle,
            color,
            backgroundColor,
            textAlign: "center",
            border: `1px solid #d0d0d0`,
            fontWeight: 600
          });
        },
        render: rowData => (
          <ComboSelect
            group="FUEL ORDER STATUS"
            subGroup=""
            value={rowData.status}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(250, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Ordered From",
        field: "companyUuid",
        emptyValue: null,
        initialEditValue: null,
        render: (rowData) => (
          <CompanyUuidLookup
            value={rowData.companyUuid}
            type="render"
            companyType="FUEL_SUPPLIER"
            hideCode={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(undefined, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Reason",
        field: "reason",
        editable: "never",
        emptyValue: "",
        fullWidth: true,
        render: rowData => (
          <ComboSelect
            group="SERVICE CANCELLATION REASONS"
            subGroup=""
            value={rowData.reason}
            displayOnly={true}
          />
        ),
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          selection: state.canEdit,
          selectionProps: rowData => ({
            disabled: !selectionAllowed(rowData),
            color: "primary"
          })
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
          Action: _props => {
            let props = {..._props};
            if (typeof props.action.action === "function") {
              props.action = {..._props.action, ..._props.action.action(_props.data)};
            }
            if (props.action.icon==="generate") {
              return (
                <Tooltip title={props.action.tooltip}>
                  <Button
                    style={{...props.action.style, height: 24, display: props.action.hidden?'none':'inherit'}}
                    variant="contained"
                    color="primary"
                    startIcon={props.action.startIcon||<PlayIcon/>}
                    className={classes.button}
                    disabled={ props.action.disableForStatus?props.action.disableForStatus.includes(props.data[0].status)||props.action.disabled:props.action.disabled}
                    onClick={() => {
                      props.action.onClick(props.data)
                    }}
                  >
                    {props.action.text}
                  </Button>
                </Tooltip>
              )
            } else if (props.action.icon) {
              if (props.action.tooltip) {
                return (<Tooltip title={props.action.tooltip}><IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton></Tooltip>)
              }
              return (<IconButton {...__.pick(props.action, ['onClick', 'style', 'color', 'disabled'])}><Icon>{props.action.icon}</Icon></IconButton>)
            } else {
              return (<></>)
            }
          }
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          (rowData) => ({
            icon: "generate",
            text: "Ignore Change",
            tooltip: "Ignore changes made to the flight criteria since ordering.",
            onClick: handleIgnoreChangesClick,
            startIcon: <Icon>redo</Icon>,
            hidden: ['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData[0].reason}`) < 0,
            disabled: !state.canEdit || ['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData[0].reason}`) < 0,
          }),
          (rowData) => ({
            icon: "generate",
            text: "Amend Order",
            tooltip: "Create a new amended order",
            startIcon: <Icon>edit</Icon>,
            onClick: handleAmendOrderClick,
            disabled: !state.canEdit || rowData.status === "REQUIRED" || ['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData[0].reason}`) < 0,
          }),
          (rowData) => ({
            icon: "generate",
            text: "Notify Supplier",
            tooltip: "Notify service provider of order cancellation",
            startIcon: <Icon>email</Icon>,
            onClick: handleSendNotificationClick,
            hidden: ['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData[0].reason}`) > -1,
            disabled: !state.canEdit || rowData.status === "REQUIRED" || ['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData[0].reason}`) > -1,
            disableForStatus: ["REQUIRED", "CANCELLED"]
          }),
          (rowData) => ({
            icon: "generate",
            text: "Cancel Requirement",
            tooltip: "Cancel previous requirements that have not been sent to supplier",
            onClick: handleCancelRequirementClick,
            hidden: ['DESTINATION', 'DEPARTURE TIME', 'ARRIVAL TIME'].indexOf(`${rowData[0].reason}`) > -1,
            disabled: !state.canEdit || rowData.status !== "REQUIRED",
            disableForStatus: ["REQUESTED", "CONFIRMED", "CANCELLED"]
          }),
        ]}
        title="Fuel Orders to Review"
        columns={getColumns({canEdit: state.canEdit})}
        data={fuelOrders.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: undefined,
        }}
      />

      {sendDialogStates.showDialog
        ?
          <SendFuelOrderDialog
            dialogStates={sendDialogStates}
            canEdit={state.canEdit}
            SystemData={SystemData}
            extraKeys={extraKeys}
            formatChars={formatChars}
            classes={classes}
            close={()=>closeSendDialog()}
          />
        :
          <></>
      }
      {addFuelDialogStates.showDialog
        ?
          <AddFuelOrderDialog
            dialogStates={addFuelDialogStates}
            SystemData={SystemData}
            extraKeys={extraKeys}
            handleAdd={handleMoveOrderLinesToNewFuelOrder}
            formatChars={formatChars}
            classes={classes}
            close={()=>closeAddFuelOrder()}
            onChange={(record)=>{
              closeAddFuelOrder();
              handleSendOrderClick(record);
            }}
          />
        :
          <></>
      }
      {sendFuelDialogStates.showDialog
        ?
          <SendFuelOrderDialog
            dialogStates={sendFuelDialogStates}
            SystemData={SystemData}
            extraKeys={extraKeys}
            formatChars={formatChars}
            classes={classes}
            close={()=>closeSendFuelOrder()}
          />
        :
          <></>
      }

    </div>
  );

}

export default FuelOrdersToReviewList;
